import c from 'classnames';
import separatedElements from 'core/components/common/separatedElements';
import { LinksConsumer } from 'core/components/linksContext';
import { button, buttonAccent, buttonShadow } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { WithStyles, ITheme } from 'core/style/interfaces';
import LecturerIcon from '../../../svg/lecturer.svg';
import React from 'react';
import { Link } from 'react-router-dom';
import ProductCardMenu from './ProductCardMenu';
import { Product } from 'site/graphql';

const styles = (theme: ITheme) =>
    createStyles({
        cardDark: {
            background: theme.colors.contrastBg,

            '& $cardContentWrap:after': {
                background:
                    'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, ' + theme.colors.contrastBg + ' 70%)',
                fallbacks: [
                    {
                        background:
                            '-moz-linear-gradient(top,' +
                            'rgb(30, 37, 47)' +
                            ' 0%, ' +
                            theme.colors.contrastBg +
                            ' 70%)' /* FF3.6-15 */,
                    },
                    {
                        background:
                            '-webkit-linear-gradient(top,' +
                            'rgb(30, 37, 47)' +
                            ' 0%, ' +
                            theme.colors.contrastBg +
                            ' 70%)' /* FF3.6-15 */,
                    },
                ],
            },

            '& $name': {
                color: theme.colors.contrastBgText,

                [theme.breakpoints.up('lg')]: {
                    '&:after': {
                        background:
                            'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, ' +
                            theme.colors.contrastBg +
                            ' 70%)',
                        fallbacks: [
                            {
                                background:
                                    '-moz-linear-gradient(top,' +
                                    'rgb(30, 37, 47)' +
                                    ' 0%, ' +
                                    theme.colors.contrastBg +
                                    ' 70%)' /* FF3.6-15 */,
                            },
                            {
                                background:
                                    '-webkit-linear-gradient(top,' +
                                    'rgb(30, 37, 47)' +
                                    ' 0%, ' +
                                    theme.colors.contrastBg +
                                    ' 70%)' /* FF3.6-15 */,
                            },
                        ],
                    },
                },
            },

            '& $desc': {
                color: theme.colors.contrastBgMedium,
            },

            '& $lecturer': {
                color: theme.colors.contrastBgText,
            },
        },
        card: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            justifyContent: 'space-between',
            alignItems: 'stretch',
            boxShadow: '0 2px 5px 0px rgba(153, 153, 153, 0.48)',
            padding: 1.2 + 'rem',
            paddingTop: 0.8 + 'rem',
            lineHeight: 1.25 + 'rem',
            maxWidth: '100%',
            boxSizing: 'border-box',
            position: 'relative',
        },
        cardContentWrap: {
            position: 'relative',
            maxHeight: 16 + 'rem',
            overflow: 'hidden',
            paddingBottom: '1rem',

            '& > *': {
                marginBottom: 1 + 'rem',
            },

            '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -1,
                left: 0,
                width: '100%',
                height: '1.5rem',
                background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #FFF 70%)',
                fallbacks: [
                    {
                        background:
                            '-moz-linear-gradient(top, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 70%)' /* FF3.6-15 */,
                    },
                    {
                        background:
                            '-webkit-linear-gradient(top, rgba(255,255,255,0) 0%,rgba(255,255,255,1) 70%)' /* Chrome10-25,Safari5.1-6 */,
                    },
                ],
            },
        },
        name: {
            fontSize: 1 + 'rem',
            margin: 0,
            paddingBottom: 1 + 'rem',
            position: 'relative',

            [theme.breakpoints.up('lg')]: {
                maxHeight: '3.8rem',
                overflow: 'hidden',

                '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -1,
                    left: 0,
                    width: '100%',
                    height: '1.5rem',
                    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #FFF 70%)',
                    fallbacks: [
                        {
                            background:
                                '-moz-linear-gradient(top, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 70%)' /* FF3.6-15 */,
                        },
                        {
                            background:
                                '-webkit-linear-gradient(top, rgba(255,255,255,0) 0%,rgba(255,255,255,1) 70%)' /* Chrome10-25,Safari5.1-6 */,
                        },
                    ],
                },
            },
        },
        desc: {
            flexGrow: 1,
            color: theme.colors.textMedium,
        },
        lecturer: {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
        },
        lecturer__icon: {
            display: 'inline-block',
            width: 0.8 + 'rem',
            height: 0.8 + 'rem',
            fill: theme.colors.accentLight,
            marginRight: 0.8 + 'rem',
        },
        moreInfo: {
            ...button(theme),
            ...buttonAccent(theme),
            ...buttonShadow,
            fontWeight: 700,
            fontSize: 1 + 'rem',
            padding: 0.75 + 'rem',
        },
    });

const ProductCard: React.StatelessComponent<IInnerProps> = ({ classes, isDark, product, fetchProducts }) => {
    const lecturers = separatedElements(
        product.lecturers.map(lecturer => `${lecturer.account.firstName} ${lecturer.account.lastName}`),
        _ => _,
    );

    return (
        <LinksConsumer>
            {links => (
                <div className={c(classes.card, isDark ? classes.cardDark : '')}>
                    <ProductCardMenu product={product} fetchProducts={fetchProducts} />
                    <div className={classes.cardContentWrap}>
                        <h3 className={classes.name} title={product.name}>
                            {product.name}
                        </h3>
                        {lecturers && (
                            <div className={classes.lecturer} title={lecturers.join('')}>
                                <LecturerIcon className={classes.lecturer__icon} />
                                <span>{lecturers}</span>
                            </div>
                        )}
                        <div className={classes.desc} title={product.shortDescription}>
                            {product.shortDescription}
                        </div>
                    </div>
                    <Link className={classes.moreInfo} to={links.accountProductDetail.url(product.id)}>
                        Смотреть
                    </Link>
                </div>
            )}
        </LinksConsumer>
    );
};

type IOutterProps = {
    isDark: boolean;
    product: Product;
    fetchProducts: () => void;
};
type IInnerProps = IOutterProps & WithStyles<typeof styles>;

export default injectSheet(styles, 'ProductCard')(ProductCard);
